import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";

import RotateDevice from "../../../components/RotateDevice/RotateDevice";
import "./TrendEntrateUscite.css";
import "../../../index.css";
import Lf23Loader from "../../../components/Lf23Loader";

const TrendEntrateUscite = ({ filters, loading: parentLoading }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [entrate, setEntrate] = useState([]);
    const [uscite, setUscite] = useState([]);
    const [chartEntrate, setChartEntrate] = useState([]);
    const [chartUscite, setChartUscite] = useState([]);
    const [totaleEntrate, setTotaleEntrate] = useState({});
    const [totaleUscite, setTotaleUscite] = useState({});
    const [filterLoading, setFilterLoading] = useState(false);
    const [selectedCcnl, setSelectedCcnl] = useState(null);
    const [isFullscreenEntrate, setIsFullscreenEntrate] = useState(false);
    const [isFullscreenUscite, setIsFullscreenUscite] = useState(false);
    const [sortConfigEntrate, setSortConfigEntrate] = useState({ key: null, direction: 'asc' });
    const [sortConfigUscite, setSortConfigUscite] = useState({ key: null, direction: 'asc' });

    const handleSortEntrate = (key) => {
        setSortConfigEntrate(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    const handleSortUscite = (key) => {
        setSortConfigUscite(prev =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    const sortedEntrate = useMemo(() => {
        if (!sortConfigEntrate.key) return entrate;
        return [...entrate].sort((a, b) => {
            const raw_a = a[sortConfigEntrate.key] ?? '';
            const raw_b = b[sortConfigEntrate.key] ?? '';
            const num_a = parseFloat(raw_a);
            const num_b = parseFloat(raw_b);
            const cmp = !isNaN(num_a) && !isNaN(num_b)
                ? num_a - num_b
                : String(raw_a).localeCompare(String(raw_b));
            return sortConfigEntrate.direction === 'asc' ? cmp : -cmp;
        });
    }, [entrate, sortConfigEntrate]);

    const sortedUscite = useMemo(() => {
        if (!sortConfigUscite.key) return uscite;
        return [...uscite].sort((a, b) => {
            const raw_a = a[sortConfigUscite.key] ?? '';
            const raw_b = b[sortConfigUscite.key] ?? '';
            const num_a = parseFloat(raw_a);
            const num_b = parseFloat(raw_b);
            const cmp = !isNaN(num_a) && !isNaN(num_b)
                ? num_a - num_b
                : String(raw_a).localeCompare(String(raw_b));
            return sortConfigUscite.direction === 'asc' ? cmp : -cmp;
        });
    }, [uscite, sortConfigUscite]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setIsFullscreenEntrate(false);
                setIsFullscreenUscite(false);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreenEntrate, isFullscreenUscite]);

    useEffect(() => {
        setFilterLoading(true);
    }, [filters]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const params = {
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                    settore: filters.settore?.map(s => s.value).join(",") || "",
                    tipoContratto: filters.tipoContratto?.map(t => t.value).join(",") || "",
                    ccnl: filters.ccnl?.map(c => c.value).join(",") || "",
                    motivoCessazione: filters.motivoCessazione?.map(m => m.value).join(",") || "",
                    chartCcnlFilter: selectedCcnl || "",
                };
                const res = await axios.get(`${API_URL}/hr-generale/trend-entrate-uscite`, { params });
                setEntrate(res.data.entrate || []);
                setUscite(res.data.uscite || []);
                setChartEntrate(res.data.chartEntrate || []);
                setChartUscite(res.data.chartUscite || []);
                setTotaleEntrate(res.data.totaleEntrate || {});
                setTotaleUscite(res.data.totaleUscite || {});
            } catch (error) {
                console.error("Errore nel caricamento dati Trend Entrate/Uscite:", error);
            } finally {
                setFilterLoading(false);
                setFilterLoading(false);
            }
        };
        fetchData();
    }, [API_URL, filters, selectedCcnl]);

    const isLoading = parentLoading || filterLoading;

    // Click sulla barra: solo setSelectedCcnl, l'useEffect fa il resto
    const handleCcnlClick = (params) => {
        if (params.componentType === 'series') {
            const ccnl = params.name;
            if (selectedCcnl === ccnl) {
                setSelectedCcnl(null);
            } else {
                setSelectedCcnl(ccnl);
            }
        }
    };

    return (
        <div className="teu-container">
            {isLoading ? (
                <Lf23Loader />
            ) : (
                <>
                    <div className={`teu-section${isFullscreenEntrate ? ' fullscreen-teu' : ''}`}>
                        <RotateDevice active={isFullscreenEntrate} />
                        <div className="teu-section-header">
                            <h3 className="teu-section-title">Entrate</h3>
                            <button
                                className="teu-fullscreen-btn"
                                onClick={() => setIsFullscreenEntrate(!isFullscreenEntrate)}
                                title={isFullscreenEntrate ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreenEntrate ? (
                                        <>
                                            <polyline points="4 14 10 14 10 20"></polyline>
                                            <polyline points="20 10 14 10 14 4"></polyline>
                                            <line x1="14" y1="10" x2="21" y2="3"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    ) : (
                                        <>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <polyline points="9 21 3 21 3 15"></polyline>
                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                        <div className="teu-section-content">
                            <div className="teu-table-wrapper">
                                <table className="teu-table">
                                    <thead>
                                        <tr>
                                            {[
                                                { label: 'Operatore', key: 'operatore' },
                                                { label: 'Ingressi', key: 'ingressi' },
                                                { label: 'Last_Rapp_Lav', key: 'lastRappLav' },
                                                { label: 'Ore Ingressi', key: 'ore' },
                                                { label: 'FTE Ingressi', key: 'fte' },
                                            ].map(({ label, key }) => (
                                                <th key={key} className="teu-th-sortable" onClick={() => handleSortEntrate(key)}>
                                                    {label}
                                                    <span className="teu-sort-icon">
                                                        {sortConfigEntrate.key === key ? (sortConfigEntrate.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedEntrate.map((row, i) => (
                                            <tr key={i}>
                                                <td className="teu-cell-name">{row.operatore}</td>
                                                <td className="teu-cell-number">{row.ingressi}</td>
                                                <td>{row.lastRappLav}</td>
                                                <td className="teu-cell-number">{row.ore.toFixed(2).replace(".", ",")}</td>
                                                <td className="teu-cell-number">{row.fte.toFixed(1).replace(".", ",")}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="teu-row-totale">
                                            <td className="teu-cell-name">Totale</td>
                                            <td className="teu-cell-number">{totaleEntrate.count}</td>
                                            <td>{totaleEntrate.lastRappLav}</td>
                                            <td className="teu-cell-number">{totaleEntrate.ore?.toFixed(2).replace(".", ",")}</td>
                                            <td className="teu-cell-number">{totaleEntrate.fte?.toFixed(1).replace(".", ",")}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            <div className="teu-chart-wrapper">
                                <h4 className="teu-chart-title">Nuovi & FTE per CCNL</h4>
                                <ReactECharts
                                    option={{
                                        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
                                        legend: {
                                            data: ["Ingressi", "FTE Ingressi"],
                                            top: 0,
                                            right: 0,
                                        },
                                        grid: { left: "3%", right: "4%", bottom: "15%", containLabel: true },
                                        xAxis: {
                                            type: "category",
                                            data: chartEntrate.map(d => d.ccnl),
                                            axisLabel: { rotate: 30 },
                                        },
                                        yAxis: { type: "value" },
                                        series: [
                                            {
                                                name: "Ingressi",
                                                type: "bar",
                                                barGap: 0,
                                                data: chartEntrate.map(d => ({
                                                    value: d.count,
                                                    itemStyle: {
                                                        opacity: selectedCcnl
                                                            ? (d.ccnl === selectedCcnl ? 1.0 : 0.3)
                                                            : 1.0
                                                    },
                                                })),
                                                itemStyle: {
                                                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                                        { offset: 0, color: "#1a3a5c" },
                                                        { offset: 1, color: "#2e6db1" },
                                                    ]),
                                                },
                                                label: { show: true, position: "top", fontWeight: "bold" },
                                            },
                                            {
                                                name: "FTE Ingressi",
                                                type: "bar",
                                                data: chartEntrate.map(d => ({
                                                    value: d.fte,
                                                    itemStyle: {
                                                        opacity: selectedCcnl
                                                            ? (d.ccnl === selectedCcnl ? 1.0 : 0.3)
                                                            : 1.0
                                                    },
                                                })),
                                                itemStyle: {
                                                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                                        { offset: 0, color: "#b5cc3e" },
                                                        { offset: 1, color: "#8fa830" },
                                                    ]),
                                                },
                                                label: { show: true, position: "top", fontWeight: "bold" },
                                            },
                                        ],
                                    }}
                                    style={{ height: "300px", width: "100%" }}
                                    notMerge={true}
                                    onEvents={{ click: handleCcnlClick }}
                                />
                            </div>

                        </div>
                    </div>

                    <div className={`teu-section${isFullscreenUscite ? ' fullscreen-teu' : ''}`}>
                        <RotateDevice active={isFullscreenUscite} />
                        <div className="teu-section-header">
                            <h3 className="teu-section-title">Uscite</h3>
                            <button
                                className="teu-fullscreen-btn"
                                onClick={() => setIsFullscreenUscite(!isFullscreenUscite)}
                                title={isFullscreenUscite ? 'Esci da tutto schermo' : 'Tutto schermo'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    {isFullscreenUscite ? (
                                        <>
                                            <polyline points="4 14 10 14 10 20"></polyline>
                                            <polyline points="20 10 14 10 14 4"></polyline>
                                            <line x1="14" y1="10" x2="21" y2="3"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    ) : (
                                        <>
                                            <polyline points="15 3 21 3 21 9"></polyline>
                                            <polyline points="9 21 3 21 3 15"></polyline>
                                            <line x1="21" y1="3" x2="14" y2="10"></line>
                                            <line x1="3" y1="21" x2="10" y2="14"></line>
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                        <div className="teu-section-content">
                            <div className="teu-table-wrapper">
                                <table className="teu-table">
                                    <thead>
                                        <tr>
                                            {[
                                                { label: 'Operatore', key: 'operatore' },
                                                { label: 'Uscite', key: 'uscite' },
                                                { label: 'Prime tipo_evento', key: 'tipoEvento' },
                                                { label: 'CCNL', key: 'ccnl' },
                                                { label: 'Ore Uscite', key: 'ore' },
                                                { label: 'FTE Uscite', key: 'fte' },
                                            ].map(({ label, key }) => (
                                                <th key={key} className="teu-th-sortable" onClick={() => handleSortUscite(key)}>
                                                    {label}
                                                    <span className="teu-sort-icon">
                                                        {sortConfigUscite.key === key ? (sortConfigUscite.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedUscite.map((row, i) => (
                                            <tr key={i}>
                                                <td className="teu-cell-name">{row.operatore}</td>
                                                <td className="teu-cell-number">{row.uscite}</td>
                                                <td>{row.tipoEvento}</td>
                                                <td>{row.ccnl}</td>
                                                <td className="teu-cell-number">{row.ore.toFixed(2).replace(".", ",")}</td>
                                                <td className="teu-cell-number">{row.fte.toFixed(1).replace(".", ",")}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="teu-row-totale">
                                            <td className="teu-cell-name">Totale</td>
                                            <td className="teu-cell-number">{totaleUscite.count}</td>
                                            <td>{totaleUscite.tipoEvento}</td>
                                            <td>{totaleUscite.ccnl}</td>
                                            <td className="teu-cell-number">{totaleUscite.ore?.toFixed(2).replace(".", ",")}</td>
                                            <td className="teu-cell-number">{totaleUscite.fte?.toFixed(1).replace(".", ",")}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            <div className="teu-chart-wrapper">
                                <h4 className="teu-chart-title">Uscite & FTE per CCNL</h4>
                                <ReactECharts
                                    option={{
                                        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
                                        legend: {
                                            data: ["Uscite", "FTE Uscite"],
                                            top: 0,
                                            right: 0,
                                        },
                                        grid: { left: "3%", right: "4%", bottom: "15%", containLabel: true },
                                        xAxis: {
                                            type: "category",
                                            data: chartUscite.map(d => d.ccnl),
                                            axisLabel: { rotate: 30 },
                                        },
                                        yAxis: { type: "value" },
                                        series: [
                                            {
                                                name: "Uscite",
                                                type: "bar",
                                                barGap: 0,
                                                data: chartUscite.map(d => ({
                                                    value: d.count,
                                                    itemStyle: {
                                                        opacity: selectedCcnl
                                                            ? (d.ccnl === selectedCcnl ? 1.0 : 0.3)
                                                            : 1.0
                                                    },
                                                })),
                                                itemStyle: {
                                                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                                        { offset: 0, color: "#1a3a5c" },
                                                        { offset: 1, color: "#2e6db1" },
                                                    ]),
                                                },
                                                label: { show: true, position: "top", fontWeight: "bold" },
                                            },
                                            {
                                                name: "FTE Uscite",
                                                type: "bar",
                                                data: chartUscite.map(d => ({
                                                    value: d.fte,
                                                    itemStyle: {
                                                        opacity: selectedCcnl
                                                            ? (d.ccnl === selectedCcnl ? 1.0 : 0.3)
                                                            : 1.0
                                                    },
                                                })),
                                                itemStyle: {
                                                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                                        { offset: 0, color: "#b5cc3e" },
                                                        { offset: 1, color: "#8fa830" },
                                                    ]),
                                                },
                                                label: { show: true, position: "top", fontWeight: "bold" },
                                            },
                                        ],
                                    }}
                                    style={{ height: "300px", width: "100%" }}
                                    notMerge={true}
                                    onEvents={{ click: handleCcnlClick }}
                                />
                            </div>
                        </div>
                    </div>

                </>

            )}
        </div>
    );
};

export default TrendEntrateUscite;
